"use client";

import { useRef } from "react";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { CTAButton } from "@/shadcn/ui/button";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import BlackPill from "@/components/getting-started/BlackPill";

import { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

export default function StopSection({
  data,
  bitunix,
  phemex,
  pionex,
}: {
  data: PageHomeSchema;
  bitunix?: boolean;
  phemex?: boolean;
  pionex?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement | any>(undefined);
  const contentRef = useRef<HTMLDivElement | any>(undefined);

  useGSAP(
    () => {
      const stopTL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.25 },
      });

      stopTL
        .from(containerRef.current, {
          y: "2rem",
          delay: 0.5,
          scrollTrigger: {
            trigger: containerRef.current,
            end: "center center",
            scrub: 1,
          },
        })
        .from(contentRef.current.children, {
          y: "2rem",
          delay: 0.5,
          stagger: 0.1,
          scrollTrigger: {
            trigger: containerRef.current,
            end: "center center",
            scrub: 1,
          },
        });
    },
    { scope: containerRef }
  );

  return (
    <section
      ref={containerRef}
      className="w-full relative z-[1] overflow-hidden bg-black mix-blend-lighten"
    >
      <div className="container-lp mx-auto relative py-[10rem]">
        <div className="flex flex-col items-center justify-center mx-auto relative z-[1]">
          <div
            ref={contentRef}
            className="w-full md:w-4/5 lg:w-[70%] flex flex-col items-center justify-center mx-auto"
          >
            {data?.bestTexts && (
              <PortableText
                value={data?.bestTexts ?? []}
                components={customRichTextComponent({
                  block: {
                    h6: ({ children }) => (
                      <BlackPill
                        text={children as string}
                        className="py-[1rem] px-[3.6rem] text-[2rem] text-[#ffffffd9] bg-[#ffffff1c] border-[#ffffff0d] uppercase"
                      />
                    ),
                    h2: ({ children }) => (
                      <h2 className="gradient-title text-[5rem] lg:text-[8rem] font-[600] tracking-[-0.2rem] lg:tracking-[-0.32rem] leading-[110%] lg:leading-[120%] text-center mt-[3rem] lg:mt-[4rem] mb-[2rem] lg:mb-[3rem]">
                        {children}
                      </h2>
                    ),
                    normal: ({ children }) => (
                      <p className="text-[2rem] font-[400] leading-[150%] text-white opacity-75 text-center lg:w-[80%] mx-auto mb-[3rem] lg:mb-[6rem]">
                        {children}
                      </p>
                    ),
                  },
                  marks: {
                    orangeFlare: ({ children }) => (
                      <span
                        className={
                          bitunix
                            ? "bitunix-shadow"
                            : phemex
                              ? "phemex-shadow"
                              : pionex
                                ? "orange-shadow"
                                : "orange-shadow"
                        }
                      >
                        {children}
                      </span>
                    ),
                  },
                })}
              />
            )}

            {data?.bestCTA && <CTAButton data={data?.bestCTA} />}
          </div>
        </div>
      </div>
    </section>
  );
}
